/* inviteprint.jsx */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { InviteSheet } from './InviteSheetComponent';
import './inviteprint.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   メインコンポーネント
   =============================================================== */

const InvitePrint = () => {
  const [searchParams] = useSearchParams();

  // URLパラメータから招待者トークンとコードを取得
  const inviter = searchParams.get('inviter');
  const code = searchParams.get('code');

  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */

  // 招待状データ関連
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */

  /**
   * 招待状データを取得
   * @param {string} inviter - 招待者トークン
   * @param {string} code - 招待コード
   */
  const fetchInvite = async (inviter, code) => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(
        `${API_URL}/api/invite-view/${inviter}/${code}/invite/`);
      if (r.data?.success) {
        setInviteData(r.data);
      } else {
        setErr('招待状データの取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('招待状データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------
     印刷関連関数
     --------------------------------------------------------------- */

  /**
   * 招待状を印刷（iframe経由で印刷ダイアログを開く）
   */
  const printInvite = () => {
    const sheet = document.querySelector('.ip-sheet');
    if (!sheet) {
      window.print();
      return;
    }

    // 現在のスタイルシートを収集
    const headStyles = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    ).map(el => el.outerHTML).join('\n');

    // 印刷用iframeを作成
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0'
    });
    document.body.appendChild(iframe);

    // 印刷実行
    const onLoad = () => {
      const w = iframe.contentWindow;
      try {
        w?.focus?.();
        w?.print?.();
      } catch (_) {}
    };
    iframe.addEventListener('load', onLoad, { once: true });

    // クリーンアップ処理
    const cleanup = () => {
      try {
        iframe.removeEventListener('load', onLoad);
      } catch (_) {}
      if (iframe?.parentNode) iframe.parentNode.removeChild(iframe);
      window.removeEventListener('afterprint', cleanup);
      try {
        iframe.contentWindow?.removeEventListener('afterprint', cleanup);
      } catch (_) {}
    };
    window.addEventListener('afterprint', cleanup);
    try {
      iframe.contentWindow?.addEventListener('afterprint', cleanup);
    } catch (_) {}
    setTimeout(cleanup, 5000);

    // 印刷用スタイル
    const printOverrides = `
      <style>
        @page { size: A4 portrait; margin: 0; }
        html, body { margin:0; padding:0; }
        .ip-modalHead{ display:none !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      </style>
    `;

    // iframeにコンテンツを書き込み
    requestAnimationFrame(() => {
      const w = iframe.contentWindow;
      const doc = w.document;
      doc.open();
      doc.write(`
        <html>
          <head>
            <meta charset="utf-8" />
            <title>招待状</title>
            ${headStyles}
            ${printOverrides}
          </head>
          <body>
            ${sheet.outerHTML}
          </body>
        </html>
      `);
      doc.close();
    });
  };

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */

  // URLパラメータ変更時に招待状データを取得
  useEffect(() => {
    if (!inviter || !code) {
      setErr('無効なURLです');
      return;
    }
    fetchInvite(inviter, code);
  }, [inviter, code]);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */

  /**
   * エラー画面を表示
   */
  const renderError = () => (
    <div className="ip-hostEventDetail">
      <div className="ip-error-alert">{err}</div>
    </div>
  );

  /**
   * ローディング画面を表示
   */
  const renderLoading = () => (
    <div className="ip-hostEventDetail">読み込み中…</div>
  );

  /**
   * ページ上部の操作帯を表示
   */
  const renderHeader = () => (
    <div className="ip-modalHead">
      <div className="ip-modalTitle">招待状プレビュー</div>
      <div className="ip-spacer" />
      <button className="ip-btn ip-btn--primary" onClick={printInvite}>印刷</button>
    </div>
  );

  /**
   * 印刷シート全体を表示（InviteSheet コンポーネントを利用）
   */
  const renderPrintSheet = () => (
    <div className="ip-printA4">
      <InviteSheet
        event={inviteData.event}
        participant={inviteData.participant}
        qrCode={code}
      />
    </div>
  );

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */

  // エラー時
  if (err) {
    return renderError();
  }

  // ローディング時
  if (loading || !inviteData) {
    return renderLoading();
  }

  return (
    <div className="ip-hostEventDetail">
      {/* ページ上部の操作帯 */}
      {renderHeader()}

      {/* 印刷領域 */}
      {renderPrintSheet()}
    </div>
  );
};

export default InvitePrint;